import { useMutation, useQuery } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import {
  AuthResponseSchema,
  ForgotPasswordInputSchema,
  LoginInputSchema,
  RegisterInputSchema,
  SignupTermsSchema,
  ResetPasswordInputSchema,
  VerifyEmailInputSchema,
} from './auth.contract'
import type {
  AuthResponse,
  ForgotPasswordInput,
  LoginInput,
  RegisterInput,
  ResetPasswordInput,
  VerifyEmailInput,
} from './auth.contract'

type MessageResponse = {
  message?: string
}

export async function login(input: LoginInput): Promise<AuthResponse> {
  const payload = LoginInputSchema.parse(input)
  const data = await apiFetch<unknown>('/auth/login', {
    method: 'POST',
    body: {
      email: payload.email,
      password: payload.password,
    },
    recaptchaToken: payload.recaptchaToken,
  })
  return AuthResponseSchema.parse(data)
}

export async function register(input: RegisterInput): Promise<AuthResponse> {
  const payload = RegisterInputSchema.parse(input)
  const data = await apiFetch<unknown>('/auth/register', {
    method: 'POST',
    body: {
      name: payload.name,
      email: payload.email,
      ...(payload.phone !== undefined ? { phone: payload.phone } : {}),
      password: payload.password,
      confirmPassword: payload.confirmPassword,
      termsAccepted: payload.termsAccepted,
      termsVersion: payload.termsVersion,
      ...(payload.referralToken ? { referralToken: payload.referralToken } : {}),
    },
    recaptchaToken: payload.recaptchaToken,
  })
  return AuthResponseSchema.parse(data)
}

export function useSignupTerms() {
  return useQuery({
    queryKey: ['auth', 'terms'],
    queryFn: async () => SignupTermsSchema.parse(await apiFetch<unknown>('/auth/terms')),
  })
}

export async function requestPasswordReset(input: ForgotPasswordInput): Promise<MessageResponse> {
  const payload = ForgotPasswordInputSchema.parse(input)
  return apiFetch<MessageResponse>('/auth/password/forgot', {
    method: 'POST',
    body: { email: payload.email },
    recaptchaToken: payload.recaptchaToken,
  })
}

export async function resetPassword(input: ResetPasswordInput): Promise<AuthResponse | null> {
  const payload = ResetPasswordInputSchema.parse(input)
  const data = await apiFetch<unknown>('/auth/password/reset', {
    method: 'POST',
    body: payload,
  })
  if (data && typeof data === 'object' && 'token' in data) {
    return AuthResponseSchema.parse(data)
  }
  return null
}

export async function verifyEmail(input: VerifyEmailInput): Promise<MessageResponse> {
  const payload = VerifyEmailInputSchema.parse(input)
  return apiFetch<MessageResponse>('/auth/email/verify', {
    method: 'POST',
    body: payload,
  })
}

export async function resendVerificationEmail(): Promise<MessageResponse> {
  return apiFetch<MessageResponse>('/auth/email/resend', { method: 'POST' })
}

export function useLoginMutation() {
  return useMutation({ mutationFn: login })
}

export function useRegisterMutation() {
  return useMutation({ mutationFn: register })
}

export function useForgotPasswordMutation() {
  return useMutation({ mutationFn: requestPasswordReset })
}

export function useResetPasswordMutation() {
  return useMutation({ mutationFn: resetPassword })
}

export function useVerifyEmailMutation() {
  return useMutation({ mutationFn: verifyEmail })
}

export function useResendVerificationEmailMutation() {
  return useMutation({ mutationFn: resendVerificationEmail })
}
